/**
 * Conversation Service
 *
 * Handles conversation CRUD operations in Firebase Firestore.
 * Collection path uses configured collection name from identifier config.
 * Example: /users/{userId}/projects/{conversationId} (when VITE_COLLECTION_NAME=projects)
 */

import { collection, doc, getDocs, query, orderBy, Timestamp, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { fetch } from "@/lib/api";
import { getIdentifierConfig } from "@/lib/identifier-config";

/**
 * Address data from Google Places autocomplete
 */
export interface AddressData {
  formattedAddress: string;
  lat: number;
  lng: number;
  placeId: string;
}

/**
 * Firebase Conversation Document structure
 * Uses dynamic field names from identifier config
 */
interface FirebaseConversationDocument {
  [key: string]: any; // Dynamic fields based on config
  user_id: string;
  address?: string | null;
  address_lat?: number | null;
  address_lng?: number | null;
  address_place_id?: string | null;
  created_at: Timestamp | ReturnType<typeof serverTimestamp>;
  updated_at: Timestamp | ReturnType<typeof serverTimestamp>;
}

/**
 * Conversation list item for UI display
 * Uses dynamic field names from identifier config
 */
export interface ConversationListItem {
  [key: string]: any; // Dynamic fields based on config
  address?: string | null;
  address_lat?: number | null;
  address_lng?: number | null;
  address_place_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Converts a Firestore Timestamp to ISO string
 */
function timestampToISO(timestamp: Timestamp | string | undefined): string {
  if (!timestamp) {
    return new Date().toISOString();
  }

  if (typeof timestamp === "string") {
    return timestamp;
  }

  // Firestore Timestamp object
  return timestamp.toDate().toISOString();
}

/**
 * Update conversation name
 * @param userId - The user ID
 * @param conversationId - The conversation/thread ID
 * @param conversationName - The new conversation name
 */
export async function updateConversationName(
  userId: string,
  conversationId: string,
  conversationName: string
): Promise<void> {
  try {
    const config = getIdentifierConfig();
    const conversationRef = doc(db(), "users", userId, config.collectionName, conversationId);

    await updateDoc(conversationRef, {
      [`${config.fieldName.replace('_id', '_name')}`]: conversationName,
      updated_at: serverTimestamp(),
    });

    console.info("✨ Conversation name updated in Firebase:", { conversationId, conversationName, collection: config.collectionName });
  } catch (error: any) {
    console.error("Failed to update conversation name in Firebase:", error);
    throw new Error(`Failed to update conversation name: ${error.message}`, { cause: error });
  }
}

/**
 * Delete a conversation and all associated data via backend API
 * Removes:
 * - Main project document
 * - Messages subcollection
 * - Checks subcollection
 * - File attachments from Storage
 *
 * @param userId - The user ID
 * @param conversationId - The conversation/thread ID to delete
 */
export async function deleteConversation(
  userId: string,
  conversationId: string
): Promise<void> {
  try {
    const config = getIdentifierConfig();

    const response = await fetch(`/db/${config.pathSegment}/${conversationId}`, {
      method: "DELETE",
      userId,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to delete conversation: ${response.status} ${response.statusText}`
      );
    }

    const result = await response.json();
    console.info("✨ Conversation deleted successfully:", result);
  } catch (error: any) {
    console.error("Failed to delete conversation:", error);
    throw new Error(`Failed to delete conversation: ${error.message}`, { cause: error });
  }
}

/**
 * Get all conversations for a user, sorted by most recent first
 * @param userId - The user ID to fetch conversations for
 * @returns Array of conversation documents sorted by updated_at DESC
 */
export async function getConversationsForUser(
  userId: string
): Promise<ConversationListItem[]> {
  try {
    const config = getIdentifierConfig();
    const conversationsRef = collection(db(), "users", userId, config.collectionName);
    const q = query(conversationsRef, orderBy("created_at", "desc"));

    const querySnapshot = await getDocs(q);

    const nameField = config.fieldName.replace('_id', '_name');

    return querySnapshot.docs.map((doc) => {
      const data = doc.data() as FirebaseConversationDocument;
      return {
        [config.fieldName]: data[config.fieldName],
        [nameField]: data[nameField],
        address: data.address || null,
        address_lat: data.address_lat || null,
        address_lng: data.address_lng || null,
        address_place_id: data.address_place_id || null,
        created_at: timestampToISO(data.created_at as Timestamp),
        updated_at: timestampToISO(data.updated_at as Timestamp),
      };
    });
  } catch (error: any) {
    console.error("Error fetching conversations from Firebase:", error);

    if (error?.code === "permission-denied") {
      throw new Error(
        "Permission denied: Please check your Firestore security rules",
        { cause: error },
      );
    }

    throw new Error(`Failed to fetch conversations: ${error.message}`, { cause: error });
  }
}
